import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createBlock as _createBlock, withCtx as _withCtx, normalizeStyle as _normalizeStyle, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-c3b777a2"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "label"
}
const _hoisted_2 = { class: "dropdown-container" }

import { FieldSize } from "@/enums/system/FieldSize";
import { IDDescriptionPair } from "@/types/generic/IDDescriptionPair";
import { DxSelectBox } from "devextreme-vue/select-box";
import { DxCustomRule } from "devextreme-vue/validator";
import { ValidatedEvent } from "devextreme/ui/validator";
import { PropType, computed, ref, watch } from "vue";
import { DxValidator } from "devextreme-vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'single-select',
  props: {
  /**
   * The number value used with `v-model` to control the state of the component.
   * @type {number}
   * @required
   */
  modelValue: {
    type: Number,
    required: true,
  },
  /**
   * The data source for the component, expected to be an array of ID-Description pairs.
   * @type {IDDescriptionPair[]}
   * @required
   */
  dataSource: {
    type: Array as PropType<IDDescriptionPair[]>,
    required: true,
  },
  /**
   * Indicates whether the specific control is read-only.
   * @type {boolean}
   * @default false
   */
  readOnly: {
    type: Boolean,
    default: false,
  },
  /**
   * The label displayed for the control.
   * @type {string}
   * @default ''
   */
  label: {
    type: String,
    default: "",
  },
  /**
   * The width of the field, defined by the `FieldSize` enum.
   * @type {FieldSize}
   * @default FieldSize.Full
   */
  width: {
    type: String as PropType<FieldSize>,
    default: FieldSize.Full,
  },
  /**
   * Determines whether to show the Clear button.
   * @type {boolean}
   * @default false
   */
  showClearButton: {
    type: Boolean,
    default: false,
  },
  /**
   * Indicates whether the specific input control is a required field.
   * @type {boolean}
   * @default false
   */
  requiredInput: {
    type: Boolean,
    default: false,
  },
  /**
   * The validation group name used for form validation.
   * @type {string}
   * @default ''
   */
  validationGroup: {
    type: String,
    required: false,
    default: "",
  },
  /**
   * Determines whether to show the search textbox.
   * @type {boolean}
   * @default false
   */
  showSearch: {
    type: Boolean,
    default: false,
  },
  /**
   * Indicates whether the specific control allows a zero value.
   * @type {boolean}
   * @default false
   */
  allowZeroValue: {
    type: Boolean,
    default: false,
  },
  /**
   * A custom validation function for validating the input field.
   * @type {Function}
   * @optional
   */
  customValidation: {
    type: Function,
    required: false,
  },
  /**
   * An optional message to display when the input is invalid.
   * @type {string}
   * @optional
   */
  invalidMessage: {
    type: String,
    required: false,
  },
  /**
   * The margin applied to the bottom of the component.
   * @type {string}
   * @default '0.5rem'
   */
  bottomMargin: {
    type: String,
    default: "0.5rem",
  },
  /**
   * Determines whether to allow resetting the validity of the input.
   * @type {boolean}
   * @default false
   */
  setValid: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether to display Peek.
   * @type {boolean}
   * @default false
   */
  displayPeek: {
    type: Boolean,
    default: false,
  },
},
  emits: [
  /**
   * Emitted when the `modelValue` is updated, typically triggered when a selection changes.
   * @event update:modelValue
   * @param {Number} value - The new value to be updated in the model.
   */
  "update:modelValue",
  /**
   * Triggered when the component is clicked.
   * This event can be used to handle user interactions or trigger actions in the parent component.
   * @event iconClicked
   */
  "iconClicked",
  /**
   * Emitted when the value of the selection changes, with the updated description.
   * @event valueChanged
   * @param {string} value - The description of the selected value (or empty string if no selection).
   */
  "valueChanged",
  /**
   * Triggered when the peek icon is clicked.
   * @event peekClick
   */
  "peekClick",
],
  setup(__props, { emit }) {

const props = __props;





const selectedValueID = computed({
  get: () => props.modelValue,
  set: (value: Number) => emit("update:modelValue", value),
});

const dropdownOpened = ref(false);
const validationErrors = ref("");
const isValid = ref(true);
const selectBoxRef = ref<InstanceType<typeof DxSelectBox> | null>(null);

watch(
  () => props.setValid,
  (value, oldValue) => {
    if (value !== oldValue) isValid.value = value;
  },
);

/** Custom validation configuration to override DevExpress default validation method */
const adapterConfig = {
  getValue: () => {
    return selectedValueID.value;
  },
  applyValidationResults: (e: ValidatedEvent) => {
    if (props.setValid) {
      isValid.value = true;
      return;
    }

    isValid.value = !!e.isValid;

    // Construct list of validation errors to be displayed to the user when they hover over the field
    validationErrors.value =
      !e.isValid && e.brokenRule?.message ? e.brokenRule.message : "";
  },
  validationRequestsCallbacks: [],
};

const requiredMessage = computed(() =>
  props.label ? `${props.label} is required` : "This field is required",
);

const inputEntered = () => {
  return props.allowZeroValue
    ? selectedValueID.value >= 0
    : selectedValueID.value > 0;
};

const onValueChanged = (e: any) => {
  if (e.value === null) {
    selectedValueID.value = -1;
  }
};

watch(
  () => selectedValueID.value,
  () => {
    const selectedIdDescriptionPair = props.dataSource.find((item) => {
      return item.id == selectedValueID.value;
    });
    emit("valueChanged", selectedIdDescriptionPair?.description ?? "");
  },
);

const dropDownOptions = computed(() => ({
  position: {
    my: "left top",
    at: "left bottom",
    of: selectBoxRef.value?.$el || undefined,
    // "flipfit" is the key: it flips to top if no space,
    // and shrinks height to fit the viewport.
    collision: "flipfit",
  },
  // Set a max height so it doesn't try to take up the whole screen
  height: "auto",
  maxHeight: 300,
  offset: { y: 1 },
  container: "body",
}));

const onPeekClick = () => {
  emit("peekClick");
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "single-select-container",
    style: _normalizeStyle({
      '--width': props.width,
      '--bottom-margin': props.bottomMargin,
    })
  }, [
    (__props.label)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("span", {
            class: _normalizeClass({
          asteriskVisible: props.requiredInput || props.customValidation,
        })
          }, _toDisplayString(__props.label), 3)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_unref(DxSelectBox), {
        ref_key: "selectBoxRef",
        ref: selectBoxRef,
        value: selectedValueID.value,
        "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((selectedValueID).value = $event)),
        opened: dropdownOpened.value,
        "onUpdate:opened": _cache[1] || (_cache[1] = ($event: any) => ((dropdownOpened).value = $event)),
        "data-source": __props.dataSource,
        "value-expr": "id",
        readOnly: props.readOnly,
        "display-expr": "description",
        width: __props.width,
        isValid: isValid.value,
        hint: validationErrors.value,
        showClearButton: __props.showClearButton,
        onValueChanged: onValueChanged,
        "search-enabled": __props.showSearch,
        "search-expr": "description",
        "drop-down-options": dropDownOptions.value,
        buttons: [
          'clear',
          {
            name: 'dropdown',
            location: 'after',
            options: {
              icon: 'spindown',
              stylingMode: 'text',
              type: 'normal',
              visible: !props.readOnly,
              elementAttr: {
                'aria-label': 'Select',
              },
              onClick: () => {
                dropdownOpened.value = !dropdownOpened.value;
              },
            },
          },
          {
            name: 'peek-contact',
            location: 'after',
            options: {
              icon: 'fas fa-eye',
              stylingMode: 'text',
              visible: selectedValueID.value > 0 && props.displayPeek,
              onClick: () => onPeekClick(),
            },
          },
        ]
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(DxValidator), {
            adapter: adapterConfig,
            "validation-group": props.validationGroup
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(DxCustomRule), {
                reevaluate: true,
                message: requiredMessage.value,
                validationCallback: inputEntered
              }, null, 8, ["message"]),
              (props.customValidation)
                ? (_openBlock(), _createBlock(_unref(DxCustomRule), {
                    key: 0,
                    reevaluate: true,
                    "validation-callback": __props.customValidation,
                    message: props.invalidMessage
                  }, null, 8, ["validation-callback", "message"]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["validation-group"])
        ]),
        _: 1
      }, 8, ["value", "opened", "data-source", "readOnly", "width", "isValid", "hint", "showClearButton", "search-enabled", "drop-down-options", "buttons"])
    ])
  ], 4))
}
}

})